import { useDesignVariant } from '@shared/designVariants/DesignVariantProvider';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Box, Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { IconPlus, IconX } from '@tabler/icons-react';

import PageContainer from '@shared/components/container/PageContainer';
import Spinner from '@shared/components/spinner/Spinner';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { actions } from '@shared/store/ducks';

import { useStyles } from './styles';
import { AgentProfileList } from './components/AgentProfileList';
import { AddAgentProfileModal } from './components/AddAgentProfileModal';
import { EditAgentProfileModal } from './components/EditAgentProfileModal';
import { ViewAgentProfileModal } from './components/ViewAgentProfileModal';
import { DeleteAgentProfileModal } from './components/DeleteAgentProfileModal';
import { useAgentProfiles } from './hooks/useAgentProfiles';
import { useFilteredAgentProfiles } from './hooks/useFilteredAgentProfiles';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { AgentProfileResponseDto } from '@shared/services/apiService';
import { CreateAssistantDialog } from '@features/Workspace/CreateAssistantDialog';
import { JobsWorkspace } from '@features/Workspace/JobsWorkspace';

const NativeAssistantsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { variant } = useDesignVariant();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentCompany = useAppSelector((state) => state.auth.authenticatedUserCompany);
  const currentUserRole = useAppSelector((state) => state.auth.authenticatedUser?.role);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AgentProfileResponseDto | null>(null);
  const [profileOverrides, setProfileOverrides] = useState<Record<string, AgentProfileResponseDto>>(
    {},
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [usingJob, setUsingJob] = useState<AgentProfileResponseDto | null>(null);

  const { data: availableProfiles, customAgentProfiles, isLoading, refetch } = useAgentProfiles();
  const visibleProfiles = useMemo(
    () =>
      (variant !== 'main' ? availableProfiles : customAgentProfiles)?.map(
        (profile) => profileOverrides[profile.id] ?? profile,
      ),
    [variant, availableProfiles, customAgentProfiles, profileOverrides],
  );
  const { filteredProfiles, totalCount, filteredCount, hasActiveSearch } = useFilteredAgentProfiles(
    visibleProfiles,
    searchQuery,
  );

  useEffect(() => {
    if (!embedded) dispatch(actions.customizer.toggleLayout('full'));
  }, [dispatch, embedded]);

  const handleAddProfile = () => {
    setSelectedProfile(null);
    setShowAddModal(true);
  };
  const handleCopyProfile = async (profile: { name: string; prompt?: string }) => {
    if (!profile.prompt) {
      toast.error(t('assistantsPage.copyPrompt.empty'));
      return;
    }

    try {
      await navigator.clipboard.writeText(profile.prompt);
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 3000,
        icon: false,
        data: {
          title: t('assistantsPage.copyPrompt.title'),
          description: t('assistantsPage.copyPrompt.description', { name: profile.name }),
        },
      });
    } catch {
      toast.error(t('assistantsPage.copyPrompt.failed'));
    }
  };

  const handleEditProfile = (profile: AgentProfileResponseDto) => {
    if (profile.type !== 'custom' || currentUserRole === UserRolesEnum.VIEWER) return;
    setSelectedProfile(profile);
    setShowEditModal(true);
  };

  const handleViewProfile = (profile: AgentProfileResponseDto) => {
    setSelectedProfile(profile);
    setShowViewModal(true);
  };

  const handleDeleteProfile = (profile: AgentProfileResponseDto) => {
    if (profile.type !== 'custom' || currentUserRole === UserRolesEnum.VIEWER) return;
    setSelectedProfile(profile);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setShowDeleteModal(false);
    setSelectedProfile(null);
  };

  const handleProfileUpdated = (profile: AgentProfileResponseDto) => {
    setSelectedProfile(profile);
    setProfileOverrides((previous) => ({ ...previous, [profile.id]: profile }));
  };

  const handleProfileAction = (profile?: AgentProfileResponseDto) => {
    if (profile) handleProfileUpdated(profile);
    refetch();
    handleCloseModals();
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <PageContainer
      title={t('assistantsPage.header.title')}
      companyName={currentCompany?.title}
      description={t('assistantsPage.header.title')}>
      <Box className={classes.main} sx={variant === 'main' ? undefined : { p: 0 }}>
        <Box className={classes.header}>
          <Typography
            component="h1"
            variant={variant === 'main' ? 'h4' : 'h3'}
            className={classes.title}>
            {variant === 'main'
              ? t('assistantsPage.header.title')
              : embedded
                ? 'Job library'
                : variant === 'v1'
                  ? 'Instructions'
                  : variant === 'v2'
                    ? 'Find an assistant'
                    : 'Job library'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<IconPlus size={20} />}
            onClick={handleAddProfile}
            disabled={currentUserRole === UserRolesEnum.VIEWER}
            className={classes.addButton}>
            {t('assistantsPage.actions.addProfile')}
          </Button>
        </Box>

        {variant !== 'main' && (
          <Typography variant="body2" color="text.secondary">
            {variant === 'v2' && !embedded
              ? 'Start with a job. Choose reusable instructions for your next assistant.'
              : 'Reusable instructions for the agents in your campaigns.'}
          </Typography>
        )}
        {totalCount > 0 && (
          <Box className={classes.toolbar}>
            <TextField
              data-testid="agent-profile-search"
              className={classes.searchField}
              size="small"
              placeholder={t('assistantsPage.toolbar.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={t('assistantsPage.modals.close')}
                      onClick={() => setSearchQuery('')}>
                      <IconX size={16} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Typography variant="body2" color="text.secondary" className={classes.countLabel}>
              {hasActiveSearch
                ? t('assistantsPage.toolbar.showingCount', {
                    filtered: filteredCount,
                    total: totalCount,
                  })
                : t('assistantsPage.toolbar.profileCount', { count: totalCount })}
            </Typography>
          </Box>
        )}

        <AgentProfileList
          profiles={filteredProfiles}
          searchQuery={searchQuery}
          totalCount={totalCount}
          onEdit={handleEditProfile}
          onCopy={handleCopyProfile}
          onUse={
            variant === 'main'
              ? undefined
              : (profile) => {
                  if (currentUserRole !== UserRolesEnum.VIEWER) setUsingJob(profile);
                }
          }
          onView={handleViewProfile}
          onDelete={handleDeleteProfile}
        />
      </Box>

      <>
        {usingJob && (
          <CreateAssistantDialog open onClose={() => setUsingJob(null)} job={usingJob} />
        )}
      </>
      <AddAgentProfileModal
        open={showAddModal}
        onClose={handleCloseModals}
        onSuccess={handleProfileAction}
      />

      <EditAgentProfileModal
        open={showEditModal}
        onClose={handleCloseModals}
        onSuccess={handleProfileAction}
        onProfileUpdated={handleProfileUpdated}
        profile={selectedProfile}
      />

      <ViewAgentProfileModal
        open={showViewModal}
        onClose={handleCloseModals}
        profile={selectedProfile}
      />

      <DeleteAgentProfileModal
        open={showDeleteModal}
        onClose={handleCloseModals}
        onSuccess={handleProfileAction}
        profile={selectedProfile}
      />
    </PageContainer>
  );
};

const AssistantsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { variant } = useDesignVariant();
  return (
    <>
      <Box hidden={variant !== 'main'} sx={{ display: variant !== 'main' ? 'none' : undefined }}>
        <NativeAssistantsPage embedded={embedded} />
      </Box>
      {variant !== 'main' && <JobsWorkspace embedded={embedded} />}
    </>
  );
};

export { AssistantsPage };
